import React, { useEffect, useState, useMemo, useRef } from "react";
import { esSoloLecturaClaro, esContratista, BannerSoloLectura } from "./permisos";

function Reportes({ usuario }) {
  const soloLectura = esSoloLecturaClaro(usuario) || esContratista(usuario);

  const [data, setData] = useState([]);
  const [busquedaNodo, setBusquedaNodo]         = useState("");
  const [filtroNodo, setFiltroNodo]             = useState("");
  const [filtroAliado, setFiltroAliado]         = useState("");
  const [filtroMes, setFiltroMes]               = useState("");
  const [filtroCiudad, setFiltroCiudad]         = useState("");
  const [filtroJefe, setFiltroJefe]             = useState("");
  const [filtroCategoria, setFiltroCategoria]   = useState("");
  const [filtroRegion, setFiltroRegion]         = useState("");
  const [filtroPrioridad, setFiltroPrioridad]   = useState([]);
  const [filtroCausaFalla, setFiltroCausaFalla] = useState([]);

  const [paginaActual, setPaginaActual] = useState(1);
  const FILAS_POR_PAGINA = 50;

  const normalizar = (txt) =>
    (txt ?? "").toString().replace(/\s+/g, " ").trim().toLowerCase();

  const formatearMes = (fecha) => {
    if (!fecha) return "";
    if (fecha.includes("T")) {
      const [anio, mes] = fecha.split("T")[0].split("-");
      return `${mes}-${anio}`;
    }
    if (fecha.includes("-") && fecha.length === 10) {
      const [anio, mes] = fecha.split("-");
      return `${mes}-${anio}`;
    }
    return fecha;
  };

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/ofensores?rol=${encodeURIComponent(usuario?.rol||"")}&cedula=${encodeURIComponent(usuario?.cedula||"")}`)
      .then(res => res.json())
      .then(d => setData(d));
  }, [usuario]);

  const opcionesCausa = useMemo(() => {
    return [...new Set(data.map(i => (i.causa_falla || "").trim()))].filter(Boolean).sort();
  }, [data]);

  // ── Cascada de opciones ──
  const datosConMes = useMemo(() =>
    data.filter(i => filtroMes.length === 0 || filtroMes.includes(formatearMes(i.mes_anio)))
  , [data, filtroMes]);
  const opcionesAliado = useMemo(() => [...new Set(datosConMes.map(i => (i.aliado||"").trim()))].filter(Boolean).sort(), [datosConMes]);

  const datosConAliado = useMemo(() =>
    datosConMes.filter(i => filtroAliado.length === 0 || filtroAliado.includes((i.aliado||"").trim()))
  , [datosConMes, filtroAliado]);
  const opcionesCiudad = useMemo(() => [...new Set(datosConAliado.map(i => (i.ciudad||"").trim()))].filter(Boolean).sort(), [datosConAliado]);

  const datosConCiudad = useMemo(() =>
    datosConAliado.filter(i => filtroCiudad.length === 0 || filtroCiudad.includes((i.ciudad||"").trim()))
  , [datosConAliado, filtroCiudad]);
  const opcionesJefe = useMemo(() => [...new Set(datosConCiudad.map(i => (i.nombre_jefe||"").trim()))].filter(Boolean).sort(), [datosConCiudad]);

  const datosConJefe = useMemo(() =>
    datosConCiudad.filter(i => filtroJefe.length === 0 || filtroJefe.includes((i.nombre_jefe||"").trim()))
  , [datosConCiudad, filtroJefe]);
  const opcionesCategoria = useMemo(() => [...new Set(datosConJefe.map(i => (i.categoria||"").trim()))].filter(Boolean).sort(), [datosConJefe]);
  const opcionesRegion    = useMemo(() => [...new Set(datosConJefe.map(i => (i.region||"").trim()))].filter(Boolean).sort(), [datosConJefe]);

  const datosConRegion = useMemo(() =>
    datosConJefe
      .filter(i => filtroCategoria.length === 0 || filtroCategoria.includes((i.categoria||"").trim()))
      .filter(i => filtroRegion.length    === 0 || filtroRegion.includes((i.region||"").trim()))
  , [datosConJefe, filtroCategoria, filtroRegion]);
  const opcionesPrioridad = useMemo(() =>
    [...new Set(datosConRegion.map(i => (i.tipo_prioridad||"").trim()))].filter(Boolean).sort()
  , [datosConRegion]);

  const opcionesMes = useMemo(() =>
    [...new Set(data.map(i => formatearMes(i.mes_anio)))].filter(Boolean)
      .sort((a,b) => { const [mA,aA]=a.split("-").map(Number),[mB,aB]=b.split("-").map(Number); return new Date(aB,mB-1)-new Date(aA,mA-1); })
  , [data]);

  const datosFiltrados = useMemo(() => {
    setPaginaActual(1);
    return data.filter(item => {
      const nodo     = normalizar(item.id_nodo);
      const mes      = formatearMes(item.mes_anio);
      const aliado   = (item.aliado      || "").trim();
      const ciudad   = (item.ciudad      || "").trim();
      const jefe     = (item.nombre_jefe || "").trim();
      const categoria= (item.categoria   || "").trim();
      const region   = (item.region      || "").trim();
      const prioridad= (item.tipo_prioridad || "").trim();
      const causaVal = (item.causa_falla || "").trim();
      const causaKey = causaVal === "" ? "(sin causa)" : causaVal.toLowerCase();
      return (
        (busquedaNodo        === "" || nodo.includes(normalizar(busquedaNodo))) &&
        (filtroNodo          === "" || nodo === normalizar(filtroNodo)) &&
        (filtroMes.length    === 0  || filtroMes.includes(mes)) &&
        (filtroAliado.length === 0  || filtroAliado.includes(aliado)) &&
        (filtroCiudad.length === 0  || filtroCiudad.includes(ciudad)) &&
        (filtroJefe.length   === 0  || filtroJefe.includes(jefe)) &&
        (filtroCategoria.length === 0 || filtroCategoria.includes(categoria)) &&
        (filtroRegion.length === 0  || filtroRegion.includes(region)) &&
        (filtroPrioridad.length === 0 || filtroPrioridad.includes(prioridad)) &&
        (filtroCausaFalla.length === 0 || filtroCausaFalla.some(f =>
          f === "(sin causa)" ? causaKey === "(sin causa)" : causaKey === f.toLowerCase()
        ))
      );
    });
  }, [data, busquedaNodo, filtroNodo, filtroAliado, filtroCiudad, filtroMes, filtroJefe, filtroCategoria, filtroRegion, filtroPrioridad, filtroCausaFalla]);

  const totalPaginas   = Math.ceil(datosFiltrados.length / FILAS_POR_PAGINA);
  const datosPagina    = datosFiltrados.slice((paginaActual - 1) * FILAS_POR_PAGINA, paginaActual * FILAS_POR_PAGINA);
  const totalOfensores = datosFiltrados.length;
  const totalAlto      = datosFiltrados.filter(i => normalizar(i.tipo_prioridad).includes("alto")).length;
  const totalMedio     = datosFiltrados.filter(i => normalizar(i.tipo_prioridad).includes("medio")).length;

  const hayFiltros = busquedaNodo || filtroNodo || filtroAliado.length > 0 || filtroCiudad.length > 0 || filtroMes.length > 0 || filtroJefe.length > 0 || filtroCategoria.length > 0 || filtroRegion.length > 0 || filtroPrioridad.length > 0 || filtroCausaFalla.length > 0;

  const limpiarFiltros = () => {
    setBusquedaNodo(""); setFiltroNodo(""); setFiltroAliado([]);
    setFiltroCiudad([]); setFiltroMes([]); setFiltroJefe([]);
    setFiltroCategoria([]); setFiltroRegion([]); setFiltroPrioridad([]); setFiltroCausaFalla([]);
  };

  const descargarCSV = () => {
    const enc = ["Categoria","Nodo","Nombre Nodo","Region","% Disp","H/Ind","Causa Falla","Causa Incumplimiento ICR","Reincidencia","Agrupacion","Observaciones","Owner","Jefe","Mes/Ano"];
    const filas = datosFiltrados.map(item => [
      item.categoria, item.id_nodo, item.NOMBRE_NODO,
      item.region || "", item.porc_dispo_real || "", item.horas_dispo || "",
      item.causa_falla || "", item.causa_incumplimiento_icr || "",
      item.recurrencia || "", item.agrupacion || "", item.observaciones || "",
      item.zone_owner || "", item.nombre_jefe || "", formatearMes(item.mes_anio)
    ]);
    const q = (v) => '"' + String(v ?? "").replace(/"/g, '""') + '"';
    const csv = [enc, ...filas].map(f => f.map(q).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `reporte_ofensores_transversal_${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const btnStyle = (activo) => ({
    padding: "6px 10px", borderRadius: "6px", border: "1px solid #cbd5e1",
    background: activo ? "#1e293b" : "#fff", color: activo ? "#fff" : "#1e293b",
    cursor: "pointer", fontWeight: activo ? "bold" : "normal"
  });

  const selStyle = (activo) => ({
    padding: "8px 10px", borderRadius: "7px",
    border: activo ? "1.5px solid #E30613" : "1.5px solid #e2e8f0",
    fontSize: "12px", color: "#0f172a", background: "#f8fafc", cursor: "pointer", width: "100%"
  });

  const labelStyle = {
    fontSize: "10px", fontWeight: "700", color: "#64748b",
    textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "4px", display: "block"
  };

  const thStyle = { padding: "8px 10px", background: "#1e293b", color: "white", whiteSpace: "nowrap", fontSize: "12px", fontWeight: "600" };
  const tdStyle = { padding: "6px 8px", fontSize: "12px", borderBottom: "1px solid #f1f5f9" };


  // ── Componente filtro multiselect con dropdown checkboxes ──────────────
  const FiltroMulti = ({ label, opciones, valor, onChange, disabled }) => {
    const [abierto, setAbierto] = React.useState(false);
    const ref = React.useRef(null);
    React.useEffect(() => {
      const cerrar = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
      document.addEventListener("mousedown", cerrar);
      return () => document.removeEventListener("mousedown", cerrar);
    }, []);
    const toggle = (v) => onChange(valor.includes(v) ? valor.filter(x => x !== v) : [...valor, v]);
    const activo = valor.length > 0;
    const lbl = { fontSize: "10px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" };
    return (
      <div ref={ref} style={{ position: "relative" }}>
        <div style={{ ...lbl, marginBottom: "4px" }}>
          {label}{activo && <span style={{ marginLeft: "5px", background: "#E30613", color: "white", borderRadius: "10px", padding: "1px 6px", fontSize: "9px" }}>{valor.length}</span>}
        </div>
        <button type="button" onClick={() => !disabled && setAbierto(p => !p)} style={{
          width: "100%", padding: "8px 10px", borderRadius: "7px",
          border: activo ? "1.5px solid #E30613" : "1.5px solid #e2e8f0",
          fontSize: "12px", color: activo ? "#E30613" : "#0f172a", background: "#f8fafc",
          cursor: disabled ? "not-allowed" : "pointer", textAlign: "left",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          opacity: disabled ? 0.5 : 1, boxSizing: "border-box"
        }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "120px" }}>
            {activo ? valor.join(", ") : "Todos"}
          </span>
          <span style={{ fontSize: "9px", flexShrink: 0 }}>{abierto ? "▲" : "▼"}</span>
        </button>
        {abierto && opciones.length > 0 && (
          <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 1000,
            background: "white", border: "1px solid #e2e8f0", borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.12)", minWidth: "200px",
            maxHeight: "230px", overflowY: "auto" }}>
            {activo && (
              <div onClick={() => { onChange([]); setAbierto(false); }}
                style={{ padding: "8px 12px", fontSize: "11px", color: "#E30613",
                  fontWeight: "600", cursor: "pointer", borderBottom: "1px solid #f1f5f9",
                  background: "#fef2f2" }}>✕ Limpiar selección</div>
            )}
            {opciones.map((op, i) => (
              <label key={i} onClick={() => toggle(op)} style={{
                display: "flex", alignItems: "center", gap: "8px", padding: "7px 12px",
                cursor: "pointer", fontSize: "12px", color: "#0f172a",
                background: valor.includes(op) ? "#fef2f2" : "white",
                borderBottom: "1px solid #f9fafb" }}>
                <input type="checkbox" checked={valor.includes(op)} onChange={() => {}}
                  style={{ accentColor: "#E30613", width: "14px", height: "14px", flexShrink: 0 }} />
                <span style={{ fontWeight: valor.includes(op) ? "600" : "400" }}>{op}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    );
  };
  // ────────────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: "20px", fontFamily: "Arial" }}>

      {/* TITULO */}
      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
        <div style={{ width: "4px", height: "24px", background: "#E30613", borderRadius: "2px" }} />
        <h2 style={{ margin: 0, color: "#0f172a", fontSize: "20px", fontWeight: "600" }}>
          Reporte Ofensores Transversal
        </h2>
        {soloLectura && <BannerSoloLectura />}
      </div>

      {/* KPIs */}
      <div style={{ display: "flex", gap: "16px", marginBottom: "20px", flexWrap: "wrap" }}>
        <div style={{ background: "#1e293b", color: "white", padding: "16px 24px", borderRadius: "12px", minWidth: "160px" }}>
          <div style={{ fontSize: "11px", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em" }}>Total Ofensores</div>
          <div style={{ fontSize: "32px", fontWeight: "800" }}>{totalOfensores}</div>
        </div>
        <div style={{ background: "#dc2626", color: "white", padding: "16px 24px", borderRadius: "12px", minWidth: "160px" }}>
          <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.8)", textTransform: "uppercase", letterSpacing: "0.07em" }}>Prioridad ALTO</div>
          <div style={{ fontSize: "32px", fontWeight: "800" }}>{totalAlto}</div>
        </div>
        <div style={{ background: "#f59e0b", color: "white", padding: "16px 24px", borderRadius: "12px", minWidth: "160px" }}>
          <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.8)", textTransform: "uppercase", letterSpacing: "0.07em" }}>Prioridad MEDIO</div>
          <div style={{ fontSize: "32px", fontWeight: "800" }}>{totalMedio}</div>
        </div>
      </div>

      {/* FILTROS */}
      <div style={{ background: "white", borderRadius: "12px", padding: "16px 20px", marginBottom: "16px", border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px", flexWrap: "wrap", gap: "10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div style={{ width: "3px", height: "18px", background: "#E30613", borderRadius: "2px" }} />
            <span style={{ fontSize: "13px", fontWeight: "700", color: "#0f172a", textTransform: "uppercase", letterSpacing: "0.05em" }}>Filtros</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: "20px", padding: "4px 12px", fontSize: "12px", color: "#475569", fontWeight: "600" }}>
              {datosFiltrados.length} registros
            </div>
            {hayFiltros && (
              <button onClick={limpiarFiltros} style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "20px", padding: "4px 12px", fontSize: "12px", color: "#E30613", fontWeight: "600", cursor: "pointer" }}>
                ✕ Limpiar filtros
              </button>
            )}
            <button onClick={descargarCSV} style={{ background: "#16a34a", color: "white", border: "none", borderRadius: "8px", padding: "7px 14px", fontSize: "12px", fontWeight: "600", cursor: "pointer" }}>
              ⬇️ Descargar CSV
            </button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: "10px" }}>

          {/* Buscar nodo — texto libre */}
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <label style={labelStyle}>Buscar Nodo</label>
            <input type="text" placeholder="Escribe un nodo..." value={busquedaNodo}
              onChange={(e) => setBusquedaNodo(e.target.value)}
              style={{ padding: "8px 10px", borderRadius: "7px", border: busquedaNodo ? "1.5px solid #E30613" : "1.5px solid #e2e8f0", fontSize: "12px", color: "#0f172a", background: "#f8fafc", outline: "none", boxSizing: "border-box", width: "100%" }} />
          </div>

          {/* Mes */}
          <FiltroMulti label="Mes" opciones={opcionesMes} valor={filtroMes}
            onChange={(v) => { setFiltroMes(v); setFiltroAliado([]); setFiltroCiudad([]); setFiltroJefe([]); setFiltroCategoria([]); setFiltroRegion([]); setFiltroPrioridad([]); setFiltroCausaFalla([]); }} />

          {/* Aliado — cascada desde mes */}
          <FiltroMulti label="Aliado" opciones={opcionesAliado} valor={filtroAliado}
            onChange={(v) => { setFiltroAliado(v); setFiltroCiudad([]); setFiltroJefe([]); setFiltroCategoria([]); setFiltroRegion([]); setFiltroPrioridad([]); setFiltroCausaFalla([]); }} />

          {/* Ciudad — cascada desde aliado */}
          <FiltroMulti label="Ciudad" opciones={opcionesCiudad} valor={filtroCiudad}
            onChange={(v) => { setFiltroCiudad(v); setFiltroJefe([]); setFiltroCategoria([]); setFiltroRegion([]); setFiltroPrioridad([]); setFiltroCausaFalla([]); }} />

          {/* Jefe — cascada desde ciudad */}
          <FiltroMulti label="Jefe Zona" opciones={opcionesJefe} valor={filtroJefe}
            onChange={(v) => { setFiltroJefe(v); setFiltroCategoria([]); setFiltroRegion([]); setFiltroPrioridad([]); setFiltroCausaFalla([]); }} />

          {/* Categoría — cascada desde jefe */}
          <FiltroMulti label="Categoría" opciones={opcionesCategoria} valor={filtroCategoria}
            onChange={(v) => { setFiltroCategoria(v); setFiltroRegion([]); setFiltroPrioridad([]); setFiltroCausaFalla([]); }} />

          {/* Región — cascada desde categoría */}
          <FiltroMulti label="Región" opciones={opcionesRegion} valor={filtroRegion}
            onChange={(v) => { setFiltroRegion(v); setFiltroPrioridad([]); setFiltroCausaFalla([]); }} />

          {/* Tipo Prioridad — cascada desde región */}
          <FiltroMulti label="Tipo Prioridad" opciones={opcionesPrioridad} valor={filtroPrioridad}
            onChange={(v) => { setFiltroPrioridad(v); setFiltroCausaFalla([]); }} />

          {/* Nodo ID — select simple cascada */}
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <label style={labelStyle}>Nodo ID</label>
            <select value={filtroNodo} onChange={(e) => setFiltroNodo(e.target.value)} style={selStyle(filtroNodo)}>
              <option value="">Todos</option>
              {[...new Set(data.map(i => i.id_nodo))].filter(Boolean).sort().map((n, i) => <option key={i} value={n}>{n}</option>)}
            </select>
          </div>

          {/* Causa Falla — multiselect */}
          <FiltroMulti label="Causa Falla" opciones={["(sin causa)", ...opcionesCausa.map(c => c.toLowerCase())]} valor={filtroCausaFalla}
            onChange={setFiltroCausaFalla} />

        </div>
      </div>

      {/* TABLA */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
          <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
            <tr>
              <th style={thStyle}>Categoria</th>
              <th style={thStyle}>Nodo</th>
              <th style={thStyle}>Nombre Nodo</th>
              <th style={thStyle}>Region</th>
              <th style={{ ...thStyle, textAlign: "center" }}>% Disp</th>
              <th style={{ ...thStyle, textAlign: "center" }}>H/Ind</th>
              <th style={thStyle}>Causa Falla</th>
              <th style={thStyle}>Causa Incumplimiento ICR</th>
              <th style={thStyle}>Reincidencia</th>
              <th style={thStyle}>Agrupacion</th>
              <th style={{ ...thStyle, minWidth: "200px" }}>Observaciones</th>
              <th style={thStyle}>Owner</th>
              <th style={thStyle}>Jefe</th>
              <th style={thStyle}>Mes/Ano</th>
            </tr>
          </thead>
          <tbody>
            {datosPagina.map((item, index) => (
              <tr key={index} style={{ background: index % 2 === 0 ? "#fff" : "#f8fafc" }}>
                <td style={{ ...tdStyle, fontWeight: "600" }}>{item.categoria}</td>
                <td style={tdStyle}>{item.id_nodo}</td>
                <td style={tdStyle}>{item.NOMBRE_NODO}</td>
                <td style={tdStyle}>{item.region || "—"}</td>
                <td style={{ ...tdStyle, textAlign: "center" }}>
                  {item.porc_dispo_real != null ? `${parseFloat(item.porc_dispo_real).toFixed(2)}%` : "—"}
                </td>
                <td style={{ ...tdStyle, textAlign: "center" }}>
                  {item.horas_dispo != null ? parseFloat(item.horas_dispo).toFixed(2) : "—"}
                </td>
                <td style={tdStyle}>{item.causa_falla || "—"}</td>
                <td style={tdStyle}>{item.causa_incumplimiento_icr || "—"}</td>
                <td style={tdStyle}>{item.recurrencia || "—"}</td>
                <td style={tdStyle}>{item.agrupacion || "—"}</td>
                <td style={{ ...tdStyle, minWidth: "200px" }}>{item.observaciones || "—"}</td>
                <td style={tdStyle}>{item.zone_owner || "—"}</td>
                <td style={tdStyle}>{item.nombre_jefe || "—"}</td>
                <td style={tdStyle}>{formatearMes(item.mes_anio)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* PAGINACION */}
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: "20px", gap: "4px", flexWrap: "wrap" }}>
        <button onClick={() => setPaginaActual(1)} disabled={paginaActual === 1} style={btnStyle(false)}>Primera</button>
        <button onClick={() => setPaginaActual(p => Math.max(1, p - 1))} disabled={paginaActual === 1} style={btnStyle(false)}>Anterior</button>
        {Array.from({ length: totalPaginas }, (_, i) => i + 1)
          .filter(p => p === 1 || p === totalPaginas || Math.abs(p - paginaActual) <= 2)
          .reduce((acc, p, i, arr) => { if (i > 0 && p - arr[i - 1] > 1) acc.push("..."); acc.push(p); return acc; }, [])
          .map((p, i) => p === "..." ? (
            <span key={i} style={{ padding: "6px 4px", color: "#94a3b8" }}>…</span>
          ) : (
            <button key={i} onClick={() => setPaginaActual(p)} style={btnStyle(p === paginaActual)}>{p}</button>
          ))}
        <button onClick={() => setPaginaActual(p => Math.min(totalPaginas, p + 1))} disabled={paginaActual === totalPaginas} style={btnStyle(false)}>Siguiente</button>
        <button onClick={() => setPaginaActual(totalPaginas)} disabled={paginaActual === totalPaginas} style={btnStyle(false)}>Ultima</button>
      </div>

    </div>
  );
}

export default Reportes;